import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { apiUrl } from '../../apiBase';

export default function AdminsManager({ schoolId, onError, onSuccess }) {
  const [admins, setAdmins] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [deleteDlgOpen, setDeleteDlgOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');

  

  const resetForm = () => { setPhone(''); setName(''); setSurname(''); };

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(
        apiUrl(`/admin/admins/${encodeURIComponent(schoolId)}`),
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('load');
      const items = await res.json();
      setAdmins(Array.isArray(items) ? items : (items.admins || []));
    } catch (e) { onError && onError('Yöneticiler yüklenemedi'); }
  }, [onError, schoolId]);

  useEffect(() => { if (schoolId) refresh(); }, [schoolId, refresh]);

  const openNew = () => { setEditing(null); resetForm(); setDlgOpen(true); };
  const openEdit = (a) => { setEditing(a); setPhone(a.phone || ''); setName(a.name || ''); setSurname(a.surname || ''); setDlgOpen(true); };
  const save = async () => {
    try {
      const phoneVal = (phone || '').trim();
      const nameVal = (name || '').trim();
      const surnameVal = (surname || '').trim();
      const isEditing = Boolean(editing && editing.phone);

      if (!phoneVal) { onError && onError('Telefon gerekli'); return; }
      if (!isEditing && (!nameVal || !surnameVal)) { onError && onError('Ad ve soyad gerekli'); return; }

      const body = isEditing
        ? { name: nameVal || undefined, surname: surnameVal || undefined }
        : { phone: phoneVal, name: nameVal, surname: surnameVal };

      let res;
      if (isEditing) {
        res = await fetch(
          apiUrl(`/admin/admins/${encodeURIComponent(schoolId)}/${encodeURIComponent(editing.phone)}`),
          { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), credentials: 'include' }
        );
      } else {
        res = await fetch(
          apiUrl(`/admin/admins/${encodeURIComponent(schoolId)}`),
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), credentials: 'include' }
        );
      }

      if (res.status === 401) { onError && onError('Yetki gerekli. Lütfen Yönetici olarak giriş yapın.'); return; }
      if (!res.ok) {
        let msg = 'Yönetici kaydedilemedi';
        try {
          const err = await res.json();
          const detail = err && (err.detail || err.message);
          if (detail) msg = String(detail);
        } catch (_) {}
        throw new Error(msg);
      }

      onSuccess && onSuccess('Yönetici kaydedildi');
      setDlgOpen(false); setEditing(null);
      refresh();
    } catch (e) {
      onError && onError((e && e.message) || 'Yönetici kaydedilemedi');
    }
  };
  const askDelete = (a) => {
    setAdminToDelete(a || null);
    setDeleteDlgOpen(true);
  };
  const closeDelete = () => {
    setDeleteDlgOpen(false);
    setAdminToDelete(null);
  };
  const remove = async () => {
    if (!adminToDelete?.phone) return;
    try {
      const res = await fetch(
        apiUrl(`/admin/admins/${encodeURIComponent(schoolId)}/${encodeURIComponent(adminToDelete.phone)}`),
        { method: 'DELETE', credentials: 'include' }
      );
      if (res.status === 401) { onError && onError('Yetki gerekli. Lütfen Yönetici olarak giriş yapın.'); return; }
      if (!res.ok) {
        let msg = 'Yönetici silinemedi';
        try {
          const err = await res.json();
          const detail = err && (err.detail || err.message);
          if (detail) msg = String(detail);
        } catch (_) {}
        throw new Error(msg);
      }
      onSuccess && onSuccess('Yönetici silindi');
      closeDelete();
      refresh();
    } catch (e) {
      onError && onError((e && e.message) || 'Yönetici silinemedi');
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Yöneticiler</Typography>
              <Button startIcon={<Add />} size="small" variant="contained" onClick={openNew}>Yeni Yönetici</Button>
            </Box>
            <List dense>
              {admins.map((a) => (
                <ListItem key={a.phone}>
                  <ListItemText
                    primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>{`${a.name || ''} ${a.surname || ''}`.trim() || a.phone}</Typography>}
                    secondary={a.phone}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => openEdit(a)}><Edit /></IconButton>
                    <IconButton edge="end" onClick={() => askDelete(a)}><Delete /></IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Yönetici Formu</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Telefon" inputProps={{ 'aria-label': 'Telefon' }} value={phone} onChange={(e) => setPhone(e.target.value)} size="small" disabled={Boolean(editing && editing.phone)} />
            <TextField label="Ad" inputProps={{ 'aria-label': 'Ad' }} value={name} onChange={(e) => setName(e.target.value)} size="small" />
            <TextField label="Soyad" inputProps={{ 'aria-label': 'Soyad' }} value={surname} onChange={(e) => setSurname(e.target.value)} size="small" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={save}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDlgOpen} onClose={closeDelete} fullWidth maxWidth="xs">
        <DialogTitle>Yöneticiyi Sil</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {adminToDelete
              ? `${adminToDelete.name || ''} ${adminToDelete.surname || ''} silinsin mi?`
              : 'Bu yönetici silinsin mi?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete}>İptal</Button>
          <Button color="error" variant="contained" onClick={remove}>Sil</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


